import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

/**
 * Keys for a single lot entry. Production dates and lot codes have a 1:1 positional correspondence
 * on the CoA, so we model them as paired entries rather than two parallel arrays.
 */
export enum VerveCoffeeCertificateOfAnalysisLotEntryKeys {
  productionDate = 'productionDate',
  lotCode = 'lotCode',
}

export const verveCoffeeCertificateOfAnalysisLotEntrySchema = yup.object().shape({
  [VerveCoffeeCertificateOfAnalysisLotEntryKeys.productionDate]: yup.string().notRequired(),
  [VerveCoffeeCertificateOfAnalysisLotEntryKeys.lotCode]: yup.string().notRequired(),
})
export type VerveCoffeeCertificateOfAnalysisLotEntry = yup.InferType<
  typeof verveCoffeeCertificateOfAnalysisLotEntrySchema
>

export const verveCoffeeCertificateOfAnalysisFileDataSchema = yup.object().shape({
  organizationLogoOverride: yup.string().notRequired(),
  coaNumber: yup.string().notRequired(),
  product: yup.string().notRequired(),
  productSku: yup.string().notRequired(),
  lotEntries: yup.array().of(verveCoffeeCertificateOfAnalysisLotEntrySchema).required(),
  bestBy: yup.string().notRequired(),
  blendOrigins: yup.string().notRequired(),
  summaryOfDeliverables: yup.string().notRequired(),
  colorScore: yup.string().notRequired(),
  aromas: yup.string().notRequired(),
  particleSize: yup.string().notRequired(),
  moistureContent: yup.string().notRequired(),
  waterActivity: yup.string().notRequired(),
})

export type VerveCoffeeCertificateOfAnalysisFileData = yup.InferType<
  typeof verveCoffeeCertificateOfAnalysisFileDataSchema
>
type RequiredFileData = MakeRequiredAndNullable<VerveCoffeeCertificateOfAnalysisFileData>

export const verveCoffeeCertificateOfAnalysisFileMappings: RequiredFileData = {
  organizationLogoOverride: '3281230c-d004-49b3-b526-a517be3fe936',
  coaNumber: 'e497fffc-4498-4e56-b417-06158599c784',
  product: '8c3ac32d-6368-4d03-94b8-4c563648216d',
  productSku: 'e13b8c50-beb8-4faf-add4-2563ee53f32d',
  lotEntries: [
    {
      productionDate: 'd73cc1ce-6ca4-486e-9770-e2d0f86f9e72',
      lotCode: '3cb2fa98-05ef-421c-9475-c2ad6657f58b',
    },
  ],
  bestBy: '5c185720-cefb-470d-8e91-933ec303d85a',
  blendOrigins: 'c96d9413-cacb-4031-993f-3f19c218bdc5',
  summaryOfDeliverables: '86a61c73-3a73-4a37-bbbb-4e9dccb9f660',
  colorScore: '43c67730-6b30-4d3e-99b8-54cda6b8afd1',
  aromas: '3b2888b8-55b0-4a45-b255-fea5c415e12e',
  particleSize: 'd5936938-70d4-45f6-aaf9-439d45189ce9',
  moistureContent: '26e9b414-c18e-4c5c-9d3d-aa76527546d1',
  waterActivity: '8e693bde-2fd8-4602-95bd-dadbe9091224',
}

export const mockVerveCoffeeCertificateOfAnalysisData: RequiredFileData = {
  organizationLogoOverride: '',
  coaNumber: 'COA0706202603034',
  product: 'West Cliff Blend, Whole Bean',
  productSku: 'CO-BL-03034-1LB-WB',
  lotEntries: [
    { productionDate: '6/15/2026', lotCode: '2630072' },
    { productionDate: '6/16/2026', lotCode: '2630064' },
    { productionDate: '6/17/2026', lotCode: '2630063' },
    { productionDate: '6/18/2026', lotCode: '2630042' },
    { productionDate: '6/26/2026', lotCode: '2630004' },
    { productionDate: '6/26/2026', lotCode: '2630005' },
    { productionDate: '6/26/2026', lotCode: '2630011' },
    { productionDate: '6/26/2026', lotCode: '2630012' },
    { productionDate: '6/27/2026', lotCode: '2630013' },
    { productionDate: '6/29/2026', lotCode: '2630028' },
  ],
  bestBy: '10/13/2026',
  blendOrigins: 'Latin America and Africa',
  summaryOfDeliverables: '9919 lbs roasted coffee in 10 Supersacks (FIBCs)',
  colorScore: '65.2',
  aromas: 'Almond, Brown Sugar, Apple',
  particleSize: 'Whole Bean',
  moistureContent: 'Less than 2% at the time of packing',
  waterActivity: 'Less than AW .200 at the time of packing',
}
